<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';

import Button from '@clovyr/bed/components/controls/Button.vue';
import NostrLogo from '@clovyr/bed/components/icons/NostrLogo.vue';
import { useMatomo } from '@clovyr/bed/composables/useMatomo';
import { getAllQueryParams } from '@clovyr/pollen/http/qs';
import type { Manifest } from '@clovyr/pollen/manifest';
import { AlreadySignedUpError } from '@clovyr/pollen/pollen/errors';
import { passkeysAvailable } from '@clovyr/pollen/user';

import { useNostr } from '@/composables/useNostr';
import { useEventBus } from '@/stores/event_bus';

import Banner from '../../../components/blocks/Banner.vue';
import FormField from '../../../components/blocks/FormField.vue';
import PasswordFormField from '../../../components/blocks/PasswordFormField.vue';
import FormWrapper from '../../../components/forms/FormWrapper.vue';
import { usePollenStore } from '../../../stores/pollen_store';
import { ShowAuthForm } from '../types';

export type SignupProps = {
  isPubFlow?: boolean;
  showAppLaunchMarketing?: boolean;
  app?: Manifest;
};
const props = defineProps<SignupProps>();

const emit = defineEmits<{
  (e: 'authForm', form: string): void;
  (e: 'close'): void;
}>();

const { eventBus } = useEventBus();
const { pollen } = usePollenStore();

const handleLogin = () => {
  emit('authForm', 'showLogin');
};

const alreadySignedUp = ref(false);
const emailParam = ref('');
const passkeyError = ref('');
const supportsPasskeys = computed(() => passkeysAvailable());
const usePassword = ref(!supportsPasskeys.value);
const submitting = ref(false);
const emailSchema = toTypedSchema(
  z.object({ email: z.string().trim().email('Enter a valid email address.') }),
);

const { isNostrAvailable } = useNostr();

const completeSignup = (method: string) => {
  const matomo = useMatomo();
  matomo.trackEvent('Auth', 'Signup', method);

  const query = getAllQueryParams();
  const redir = query.get('redir');
  if ((redir && redir.includes('publishers')) || props.isPubFlow) {
    // skip recovery kit
    emit('close');
  } else {
    emit('authForm', 'showRecoveryKitDownload');
    eventBus.emit('auth:signup:complete');
  }
};

const onSubmit = async (values) => {
  if (submitting.value) return;
  emailParam.value = values.email.trim();
  if (!usePassword.value) return signUpWithPasskey();
  submitting.value = true;
  try {
    await pollen.signUp(values.email, values.password);
    completeSignup('Password');
    // no confirmation screen for now, just close the dialog
  } catch (err) {
    if (err instanceof AlreadySignedUpError) {
      alreadySignedUp.value = true;
      return;
    }
    throw err;
  } finally {
    submitting.value = false;
  }
};

const signUpWithPasskey = async () => {
  submitting.value = true;
  passkeyError.value = '';
  const email = emailParam.value.trim();
  if (!email) {
    passkeyError.value = 'Enter your email before creating a passkey.';
    submitting.value = false;
    return;
  }

  try {
    await pollen.signUpWithPasskey(email, 'Clovyr passkey');
    completeSignup('Passkey');
  } catch (err) {
    if (err instanceof AlreadySignedUpError) {
      alreadySignedUp.value = true;
      return;
    }
    passkeyError.value = 'Passkey sign-up failed. Please try again.';
    console.warn(err);
  } finally {
    submitting.value = false;
  }
};

const signUpWithNostr = async () => {
  emit('authForm', ShowAuthForm.NostrEmail);
};
const schema = inject<Record<'email' | 'password', string> | undefined>('validationSchema');
</script>

<template>
  <div class="form-wrapper form-auth">
    <div class="form-heading">Sign Up</div>
    <div class="form-description sub-heading">
      {{
        usePassword
          ? 'Create your account with an email and password.'
          : 'Create your account with a passkey. No password to remember.'
      }}
    </div>
    <banner v-if="isPubFlow" status="warning" class="banner">
      In order to access the Publisher Portal, you must first register for a Clovyr account.
    </banner>

    <Banner status="critical" v-if="alreadySignedUp">
      You have already signed up for an account on this device, please
      <span class="button-inline button-primary" @click="handleLogin">log in</span>
      instead.
    </Banner>

    <div class="form">
      <FormWrapper
        :submit-label="
          submitting ? 'Creating account…' : usePassword ? 'Create account' : 'Sign up with passkey'
        "
        :validation-schema="usePassword ? schema : emailSchema"
        :disabled="submitting"
        @submit="onSubmit"
      >
        <template v-slot:fields>
          <FormField
            name="email"
            label="Email"
            type="email"
            autocomplete="username"
            shape="cubed"
            v-model="emailParam"
          />
          <PasswordFormField
            v-if="usePassword"
            name="password"
            label="Password"
            type="password"
            shape="cubed"
            reveal
            password-validate
          />
        </template>
      </FormWrapper>

      <div v-if="supportsPasskeys" class="form-callout">
        <button
          type="button"
          class="auth-method-link button-inline button-primary"
          :disabled="submitting"
          @click="
            usePassword = !usePassword;
            passkeyError = '';
          "
        >
          {{ usePassword ? 'Sign up with passkey instead' : 'Sign up with password instead' }}
        </button>
      </div>

      <Banner status="critical" v-if="passkeyError" class="passkey-error">
        {{ passkeyError }}
      </Banner>

      <Button
        v-if="isNostrAvailable"
        class="button nostr-button"
        label="Sign up with Nostr"
        @click="signUpWithNostr"
      >
        <NostrLogo />
      </Button>

      <div class="form-callout">
        <span>Have an account?</span
        ><span class="button-inline button-primary" @click="handleLogin">Log in</span>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';

.form-wrapper {
  // max-width: 30rem;
  padding-top: 10px;
}
.form-callout {
  > * {
    margin-left: 0.25rem;
  }
}
.sub-heading {
  max-width: 35rem;
}
.button {
  height: 2.85rem;
}

.nostr-button {
  background-color: #995ada;
  color: white;
  width: 100%;
  margin-top: 1rem;

  svg {
    width: 3rem;
    height: 2rem;
  }

  &:hover {
    background-color: colors.color(active, primary);
  }
}

.auth-method-link {
  border: 0;
  background: none;
  font: inherit;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.passkey-error {
  margin-top: 1rem;
}

:deep(.textinput-block--password-validate) {
  padding-bottom: unset;
}
</style>
